import React, { PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBar, View } from 'react-native';

import AsyncStorage from '@react-native-async-storage/async-storage';

import { EntryRuleFragment, useGetEntryAndFeedConfigsQuery } from '@/api/limits-and-rules/query.generated';
import { ds } from '@/components/lib/design.system';
import { useLocalFlagsStore } from '@/feature/devsettings/hooks/use-dev-settings';
import { useWhatsNewStore } from '@/feature/whats-new/hooks/use-whats-new-store';
import { useAppResumeEffect } from '@/hooks/use-resume';
import { common } from '@/styles/styles';
import { EntryFeedConfig, GameMode, GameType } from '@/types/api.generated';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { useJurisdictionStore } from './use-jurisdiction';
import { useUserStore } from './use-user-store';

type State = {
    allEntryRules: EntryRuleFragment[];
    entryFeedConfig?: EntryFeedConfig;
    _hydrated: boolean;
    applicantId: string;
    actions: {
        setAllEntryRulesAndConfigs: (entryRules: EntryRuleFragment[], entryFeedConfig?: EntryFeedConfig) => void;
        setHydrated: () => void;
        setApplicantId: (applicantId: string) => void;
    };
};

const defaultEntryRules = {
    minNumberOfPicks: 0,
    maxNumberOfPicks: 0,
    minNumberOfTeams: 0,
    maxNumberOfPicksForType: [],
    gameType: GameType.House,
    gameMode: GameMode.Perfect,
};

export const useGlobalState = create<State>()(
    persist(
        set => ({
            multipliers: {},
            allEntryRules: [defaultEntryRules],
            entryFeedConfig: undefined,
            _hydrated: false,
            applicantId: '',
            actions: {
                setAllEntryRulesAndConfigs: (entryRules, entryFeedConfig) => {
                    set({ allEntryRules: entryRules, entryFeedConfig: entryFeedConfig });
                },
                setHydrated: () => {
                    set({ _hydrated: true });
                },
                // This action and flag are added to the GlobalState to ensure that these values are available on the Web App even after the user is redirected from IdComply site in IDPV flow
                setApplicantId: (applicantId: string) => {
                    set({ applicantId });
                },
            },
        }),
        {
            name: 'global-storage',
            version: 2,
            storage: createJSONStorage(() => AsyncStorage),
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            partialize: ({ actions, _hydrated, ...rest }: State) => rest,
            onRehydrateStorage: () => store => {
                store?.actions.setHydrated();
            },
        }
    )
);

export const GlobalStateProvider = ({ children }: PropsWithChildren) => {
    const jurisdiction = useJurisdictionStore(state => state.jurisdiction);
    const [{ data: tradingLimitsAndEntryRules }, fetchTradingLimitsAndRules] = useGetEntryAndFeedConfigsQuery({
        pause: !jurisdiction,
    });
    const { ready: isPhraseLoaded } = useTranslation();

    const session = useUserStore(state => state.session);
    const guest = useUserStore(state => state.guest);

    useAppResumeEffect(
        useCallback(() => {
            //postpone request until jurisdiction is available
            //fetch when the session is hydrated or user is guest
            if (jurisdiction && (session || guest)) {
                fetchTradingLimitsAndRules({ requestPolicy: 'cache-and-network' });
            }
        }, [fetchTradingLimitsAndRules, guest, jurisdiction, session])
    );

    useEffect(() => {
        //postpone request until jurisdiction is available
        //fetch when the session is hydrated or user is guest
        if (jurisdiction && (session || guest)) {
            fetchTradingLimitsAndRules({ requestPolicy: 'cache-and-network' });
        }
    }, [fetchTradingLimitsAndRules, guest, jurisdiction, session]);

    const allEntryRulesAndConfigs = useMemo(() => {
        return {
            allEntryRules: tradingLimitsAndEntryRules?.getAllEntryRules ?? [defaultEntryRules],
            entryFeedConfig: tradingLimitsAndEntryRules?.getEntryFeedConfig,
        };
    }, [tradingLimitsAndEntryRules?.getAllEntryRules, tradingLimitsAndEntryRules?.getEntryFeedConfig]);

    useEffect(() => {
        const { setAllEntryRulesAndConfigs } = useGlobalState.getState().actions;
        setAllEntryRulesAndConfigs(allEntryRulesAndConfigs.allEntryRules, allEntryRulesAndConfigs.entryFeedConfig);
    }, [allEntryRulesAndConfigs]);

    const persistedJurisdictionDataLoaded = useJurisdictionStore(state => state._hydrated);
    const persistedLocalFlagsLoaded = useLocalFlagsStore(state => state._hydrated);
    const persistedGlobalDataLoaded = useGlobalState(state => state._hydrated);
    const persistedWhatsNewDataLoaded = useWhatsNewStore(state => state._hydrated);

    const persistedDataLoaded =
        persistedJurisdictionDataLoaded &&
        persistedLocalFlagsLoaded &&
        persistedGlobalDataLoaded &&
        persistedWhatsNewDataLoaded;

    //only render the content when all persisted data is loaded
    //and when phrase resources are loaded
    const renderContent = persistedDataLoaded && isPhraseLoaded;

    if (!renderContent) {
        return (
            <View
                style={[
                    { backgroundColor: ds.colorValue.background.brand.strong },
                    common.paddingVertical,
                    common.flex,
                    common.justifyCenter,
                ]}
            >
                <StatusBar
                    barStyle={'light-content'}
                    translucent
                    backgroundColor={ds.colorValue.background.brand.strong}
                />
            </View>
        );
    }
    return <>{children}</>;
};
